import { computed, isRef, ref, ComputedRef, Ref } from 'vue';

export type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

const unwrapMaybeRef = <T>(source: MaybeRef<T>): T => (isRef(source) ? source.value : source) as T;

export interface UseSelectAllOptions<T> {
  items: MaybeRef<T[]>;
  isSelected?: (item: T) => boolean;
  setSelected: (item: T, selected: boolean) => void;
  /**
   * Called before selecting all, e.g. to load remaining paginated items.
   * Only invoked when selecting (not when deselecting).
   */
  ensureAllLoaded?: () => Promise<void> | void;
  onSelectionChange?: (selectedItems: T[]) => void;
}

export interface UseSelectAllReturn<T> {
  selectedItems: ComputedRef<T[]>;
  allSelected: ComputedRef<boolean>;
  someSelected: ComputedRef<boolean>;
  selectAll: ComputedRef<boolean>;
  isUpdating: Ref<boolean>;
  setAllSelected: (selected: boolean) => Promise<void>;
  selectAllItems: () => Promise<void>;
  deselectAll: () => Promise<void>;
  toggleItem: (item: T) => void;
}

const hasSelectedFlag = <T>(item: T): boolean => Boolean((item as T & { selected?: boolean }).selected);

export function useSelectAll<T>(options: UseSelectAllOptions<T>): UseSelectAllReturn<T> {
  const isUpdating = ref(false);
  const pendingSelectAll = ref<boolean | null>(null);

  const items = computed<T[]>(() => unwrapMaybeRef(options.items) ?? []);
  const isItemSelected = options.isSelected ?? hasSelectedFlag;

  const selectedItems = computed<T[]>(() => items.value.filter(isItemSelected));

  const allSelected = computed(() => items.value.length > 0 && selectedItems.value.length === items.value.length);

  const someSelected = computed(() => selectedItems.value.length > 0 && !allSelected.value);

  const notifySelectionChange = () => {
    options.onSelectionChange?.(selectedItems.value);
  };

  const applySelection = (selected: boolean) => {
    for (const item of items.value) {
      if (isItemSelected(item) !== selected) {
        options.setSelected(item, selected);
      }
    }
    notifySelectionChange();
  };

  const setAllSelected = async (selected: boolean) => {
    if (isUpdating.value) {
      return;
    }

    isUpdating.value = true;
    pendingSelectAll.value = selected;

    try {
      if (selected && options.ensureAllLoaded) {
        await options.ensureAllLoaded();
      }
      applySelection(selected);
    } finally {
      pendingSelectAll.value = null;
      isUpdating.value = false;
    }
  };

  const selectAll = computed(() => pendingSelectAll.value ?? allSelected.value);

  const toggleItem = (item: T) => {
    options.setSelected(item, !isItemSelected(item));
    notifySelectionChange();
  };

  return {
    selectedItems,
    allSelected,
    someSelected,
    selectAll,
    isUpdating,
    setAllSelected,
    selectAllItems: () => setAllSelected(true),
    deselectAll: () => setAllSelected(false),
    toggleItem,
  };
}
